import type { App } from 'vue'
import { reactive } from 'vue'

import type { Handler } from 'mitt'
import mitt from 'mitt'

import { Logger } from './logger';

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $auth: AuthenticationPlugin
        $user: any
    }
}

export interface AuthenticationSessionData {
    identity: any
    token: string

    [key: string]: any
}

export enum AuthenticationEvent {
    LOGIN_ATTEMPT_FAIL = 'login-attempt-fail',
    LOGIN_ATTEMPT_SUCCESS = 'login-attempt-success',
    REFRESH_ATTEMPT_FAIL = 'refresh-attempt-fail',
    REFRESH_ATTEMPT_SUCCESS = 'refresh-attempt-success',
    LOGIN = 'login',
    LOGOUT = 'logout'
}

export interface AuthenticationAccountType {

    login(credentials: Record<string, any>): Promise<AuthenticationSessionData>
    logout?(session: AuthenticationSessionData): Promise<boolean>
    refreshToken?(session: AuthenticationSessionData): Promise<AuthenticationSessionData>
    refreshIdentity?(session: AuthenticationSessionData): Promise<any>
}

export interface AuthenticationSession {
    accountType: string
    data: AuthenticationSessionData
}

export interface AuthenticationPluginOptions {
    engine?: Storage
    logger?: Logger
    sessionKey?: string
}

export class AuthenticationPlugin {

    protected emitter = mitt();

    protected engine: Storage = window.localStorage;
    protected logger: Logger = null;

    protected sessionKey: string = 'session';
    protected accountTypes: Map<string, AuthenticationAccountType> = new Map();

    protected data = reactive({
        session: null
    } as { session: AuthenticationSession });

    constructor(options?: AuthenticationPluginOptions) {

        if(options){

            if(options.engine){
                this.engine = options.engine;
            }

            if(options.logger){
                this.logger = options.logger;
            }

            if(options.sessionKey){
                this.sessionKey = options.sessionKey;
            }
        }
    }

    on(event: AuthenticationEvent, callback: Handler): this {

        this.emitter.on(event, callback);
        return this;
    }

    off(event: AuthenticationEvent, callback: Handler): this {

        this.emitter.off(event, callback);
        return this;
    }

    accountType(name: string, account: AuthenticationAccountType): this {

        this.accountTypes.set(name, account);
        return this;
    }

    getAccountType(name: string): AuthenticationAccountType {

        return this.accountTypes.get(name);
    }

    get session(): AuthenticationSessionData {

        return this.data.session?.data;
    }

    get sessionAccountType(): string {

        return this.data.session?.accountType;
    }

    get loggedIn(): boolean {

        return !!this.data.session;
    }

    setSession(session: AuthenticationSessionData, accountType: string) {

        this.data.session = { accountType, data: session };
        this.storeSession();
    }

    clearSession() {

        this.data.session = null;

        this.engine.setItem(this.sessionKey, null);

        this.logger?.info('Session cleared');
    }

    loginSession(session: AuthenticationSessionData, accountTypeName: string) {

        this.setSession(session, accountTypeName);

        this.emitter.emit(AuthenticationEvent.LOGIN_ATTEMPT_SUCCESS, {
            session: this.session
        });

        this.emitter.emit(AuthenticationEvent.LOGIN, {
            session: this.session
        });
    }

    async login(accountTypeName: string, credentials: any): Promise<AuthenticationSessionData> {

        let accountType = this.accountTypes.get(accountTypeName);
        if(!accountType){
            throw 'Invalid account type provided';
        }

        if(this.loggedIn){
            this.clearSession();
        }

        try {

            const session = await accountType.login(credentials);
            if(!session){
                throw 'No session';
            }

            this.loginSession(session, accountTypeName);

            this.logger?.info('Logged in');
        }
        catch(e) {

            this.emitter.emit(AuthenticationEvent.LOGIN_ATTEMPT_FAIL, {
                credentials: credentials,
                session: null
            });

            throw e;
        }

        return this.session;
    }

    async logout() {

        const session = this.session;
        if(!session){
            throw 'No active session';
        }

        let accountType = this.getAccountType(this.sessionAccountType);
        if(!accountType){
            throw 'Invalid account type provided';
        }

        let result = true;

        if(accountType.logout){
            result = await accountType.logout(session);
        }

        this.emitter.emit(AuthenticationEvent.LOGOUT, { session });
        this.clearSession();

        return result;
    }

    async refreshToken() {

        let session = this.session;
        if(!session){
            throw 'No active session';
        }

        const accountTypeName = this.sessionAccountType;
        let accountType = this.getAccountType(accountTypeName);

        if(!accountType){
            throw 'Invalid account type provided';
        }

        try {

            session = await accountType.refreshToken(session);
            this.setSession(session, accountTypeName);

            this.emitter.emit(AuthenticationEvent.REFRESH_ATTEMPT_SUCCESS, { session });

            this.logger?.info('Refreshed token');

            return session;
        }
        catch(e) {

            this.emitter.emit(AuthenticationEvent.REFRESH_ATTEMPT_FAIL, {
                session: null
            });

            throw e;
        }
    }

    async refreshIdentity() {

        const session = this.session;
        if(!session){
            throw 'No active session';
        }

        let accountType = this.getAccountType(this.sessionAccountType);
        if(!accountType){
            throw 'Invalid account type provided';
        }

        if(!accountType.refreshIdentity){
            return session.identity;
        }

        const newIdentity = await accountType.refreshIdentity(session);
        if(!newIdentity){
            throw 'Unable to refresh identity'
        }

        session.identity = newIdentity;
        this.storeSession();
    }

    storeSession() {

        if(!this.data.session){
            return;
        }

        this.engine.setItem(this.sessionKey, JSON.stringify(this.data.session));
    }

    restoreSession() {

        const storedSession = this.engine.getItem(this.sessionKey);
        const session = storedSession ? JSON.parse(storedSession) : null;

        if(session){

            this.data.session = session;
            this.logger?.info('Restored session');
        }
    }

    install(app: App) {

        const plugin = this;
        app.config.globalProperties.$auth = this;

        app.mixin({

            computed: {

                $user: {

                    get() {

                        const session = plugin.session;
                        if(!session){
                            return null;
                        }

                        return session.identity;
                    },

                    set(user) {

                        const session = plugin.session;
                        if(!session){
                            return null;
                        }

                        session.identity = user;
                        plugin.storeSession();
                    }
                }
            }
        })
    }
}